import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { resource } from '../utils/resource/old';
import { Availabilities, AvailabilitiesResponse, DayData, SingleAvailabilityResponse } from './interfaces/availabilities';

// DTO from backend - actual response is in camelCase
interface AvailabilityPublicDto {
  id: string;
  day: string;
  time: string;
  enabledLanguages?: Array<{
    id: string;
    name: string;
    code: string;
    color?: string;
    created_at?: string;
    updated_at?: string;
    category_id?: string;
    visible?: boolean;
    hardcoded?: boolean;
    name_translations?: {
      de: string;
      en: string;
      it: string;
    };
  }>;
  enabled_languages?: Array<{
    id: string;
    name: string;
    code: string;
    color?: string;
    created_at?: string;
    updated_at?: string;
    category_id?: string;
    visible?: boolean;
    hardcoded?: boolean;
    name_translations?: {
      de: string;
      en: string;
      it: string;
    };
  }>;
  minParties?: number;
  min_parties?: number;
  maxParties?: number;
  max_parties?: number;
  occupiedCount?: number;
  occupied_count?: number;
  experienceId?: string;
  experience_id?: string;
  created_at?: string;
  updated_at?: string;
  active?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AvailabilitiesService {

  constructor(private http: HttpClient) { 

  }
   // Metodo per calcolare il minimo numero di adulti richiesti
   public getMinAdults(availability: Availabilities): number {
    return availability.min_parties > availability.occupied_count
      ? availability.min_parties
      : 0;
  }

  // Metodo per calcolare il massimo numero di adulti consentiti
  public getMaxAdults(availability: Availabilities): number {
    return availability.max_parties;
  }
    getAvailabilities(experienceId: string,
      startDate: string,
      endDate: string,
      enableLangIso: string,
      lang: string = 'it'): Observable<DayData[]> {
      // Nuovo backend usa camelCase per i parametri
      const params = {
        dayStart: startDate,
        dayEnd: endDate,
        enableLangIso: enableLangIso
        // lang non più necessario se enableLangIso è sufficiente
      };
  
      const url = `wsb2c://availability/{experienceId}`;
  
      return resource(url)
        .params({experienceId, ...params})  
        .get<any>()
        .pipe(
          map(response => {
            // Backend returns array directly, not wrapped in data
            const responseArray = Array.isArray(response) ? response : (response.data || []);
            return responseArray.map((dayItem: any) => this.mapBackendDayDataToDayData(dayItem));
          })
        );
    }
  // Modifica il ritorno della funzione per essere AvailabilitiesResponse
  public getSingleAvailability(
    experienceId: string,
    day: string,
    time: string,
    enableLangIso: string,
    lang: string = 'it'
  ): Observable<SingleAvailabilityResponse> { 
    const params = {
      day: day,
      time: time,
      enableLangIso: enableLangIso,
      lang: lang
    };
  
    const url = `wsb2c://availability/single/{experienceId}`;
  
    return resource(url)
      .params({experienceId, ...params})
      .get<AvailabilityPublicDto>()
      .pipe(
        map(response => ({
          data: this.mapAvailabilityPublicDtoToAvailabilities(response)
        })),
        tap(response => console.log('API Response:', response))
      );
  }

  private mapBackendDayDataToDayData(backendDayData: any): DayData {
    return {
      day: backendDayData.day || '',
      availabilities: (backendDayData.availabilities || []).map((avail: any) => this.mapBackendAvailabilityToAvailabilities(avail)),
      date: backendDayData.day || '',
      available: true,
      availableDate: backendDayData.day || '',
      availableTime: ''
    };
  }

  private mapBackendAvailabilityToAvailabilities(backendAvail: any): Availabilities {
    // Backend returns camelCase (enabledLanguages, minParties, etc.)
    // Frontend expects snake_case (enabled_languages, min_parties, etc.)
    return {
      id: backendAvail.id || '',
      day: backendAvail.day || '',
      time: backendAvail.time || '',
      enabled_languages: (backendAvail.enabledLanguages || backendAvail.enabled_languages || []).map((lang: any) => ({
        id: lang.id || lang.code || '',
        name: lang.name || lang.code || '',
        color: lang.color || '',
        created_at: lang.created_at || '',
        updated_at: lang.updated_at || '',
        category_id: lang.category_id || '',
        visible: lang.visible !== undefined ? lang.visible : true,
        hardcoded: lang.hardcoded !== undefined ? lang.hardcoded : false,
        code: lang.code || '',
        name_translations: lang.name_translations || { de: '', en: '', it: '' }
      })),
      min_parties: backendAvail.minParties !== undefined ? backendAvail.minParties : (backendAvail.min_parties || 0),
      max_parties: backendAvail.maxParties !== undefined ? backendAvail.maxParties : (backendAvail.max_parties || 0),
      occupied_count: backendAvail.occupiedCount !== undefined ? backendAvail.occupiedCount : (backendAvail.occupied_count || 0),
      experience_id: backendAvail.experienceId || backendAvail.experience_id || '',
      created_at: backendAvail.created_at || '',
      updated_at: backendAvail.updated_at || '',
      active: backendAvail.active !== undefined ? backendAvail.active : true
    };
  }

  private mapAvailabilityPublicDtoToAvailabilities(dto: any): Availabilities {
    // Backend returns camelCase (enabledLanguages, minParties, etc.)
    // Frontend expects snake_case (enabled_languages, min_parties, etc.)
    const enabledLanguages = dto.enabledLanguages || dto.enabled_languages || [];
    
    return {
      id: dto.id || '',
      day: dto.day || '',
      time: dto.time || '',
      enabled_languages: enabledLanguages.map((lang: any) => ({
        id: lang.id || lang.code || '',
        name: lang.name || lang.code || '',
        color: lang.color || '',
        created_at: lang.created_at || '',
        updated_at: lang.updated_at || '',
        category_id: lang.category_id || '',
        visible: lang.visible !== undefined ? lang.visible : true,
        hardcoded: lang.hardcoded !== undefined ? lang.hardcoded : false,
        code: lang.code || '',
        name_translations: lang.name_translations || { de: '', en: '', it: '' }
      })),
      min_parties: dto.minParties !== undefined ? dto.minParties : (dto.min_parties || 0),
      max_parties: dto.maxParties !== undefined ? dto.maxParties : (dto.max_parties || 0),
      occupied_count: dto.occupiedCount !== undefined ? dto.occupiedCount : (dto.occupied_count || 0),
      experience_id: dto.experienceId || dto.experience_id || '',
      created_at: dto.created_at || '',
      updated_at: dto.updated_at || '',
      active: dto.active !== undefined ? dto.active : true
    };
  }
  
}


